import { FC, ReactNode } from 'react'
import { Tooltip } from 'antd'
import { Colors, TooltipPlacement } from '../../util/types'
import { PerSpacerProps } from '../Spacer'
import { marginClasses } from '../../util/less-util'
import { icons } from '../Icon/icons'
import PerIcon from '../Icon'
import './index.less'
import { useTranslation } from 'react-i18next'

export type isProcessingButton = {
  active: boolean
  text?: string
  tooltip?: string | false
}

export interface PerButtonProps extends PerSpacerProps, React.ButtonHTMLAttributes<HTMLButtonElement> {
  text: string
  color?: Colors
  variant?: 'pastel'
  size?: 'small' | 'medium'
  width?: 'fullWidth'
  disabled?: boolean
  before?: keyof typeof icons
  after?: keyof typeof icons
  shadow?: boolean
  tooltip?: string | ReactNode
  tooltipPlacement?: TooltipPlacement
  asLink?: string
  isProcessing?: isProcessingButton
  className?: string
  textColor?: string
  underlineText?: boolean
}

const PerButton: FC<PerButtonProps> = ({
  color,
  variant,
  size,
  width,
  before,
  after,
  text,
  disabled,
  shadow,
  tooltip,
  tooltipPlacement,
  asLink,
  isProcessing,
  className,
  textColor,
  underlineText,
  m,
  mx,
  my,
  mt,
  mr,
  mb,
  ml,
  ...props
}) => {
  const [t] = useTranslation()
  const btn = 'per-btn'
  const modifiers = marginClasses(m, mb, mt, ml, mr, mx, my, 'per-spacer')
  const processingIsActive = isProcessing && isProcessing.active

  if (color) {
    modifiers.push(`${btn}--${color}`)
  }
  if (variant) {
    modifiers.push(`${btn}--${variant}`)
  }
  if (size) {
    modifiers.push(`${btn}--${size}`)
  }
  if (width) {
    modifiers.push(`${btn}--${width}`)
  }
  if (shadow) {
    modifiers.push(`${btn}--shadow`)
  }
  if (disabled || processingIsActive) {
    modifiers.push(`${btn}--disabled`)
  }
  if (className) {
    modifiers.push(className)
  }

  const iconSize = () => {
    if (size === 'small') {
      return 10 // @btn-small_icon-size
    } else {
      return 16 // @btn_icon-size
    }
  }

  const renderTooltip = () => {
    if (isProcessing && isProcessing.active) {
      if (isProcessing.tooltip === false) {
        return undefined
      } else if (isProcessing.tooltip) {
        return isProcessing.tooltip
      } else {
        return ''
      }
    } else if (!!tooltip) {
      return tooltip
    } else {
      return undefined
    }
  }
  const renderText = () => {
    if (isProcessing && isProcessing.active) {
      return isProcessing.text ? isProcessing.text : t('global.button.isProcessing.processing')
    } else {
      return text
    }
  }
  const renderBeforeIcon = () => {
    if (isProcessing && isProcessing.active) {
      return <PerIcon name="loading_fill" size={iconSize()} spin />
    } else if (before) {
      return <PerIcon name={before} size={iconSize()} variant="clickable" />
    } else {
      return undefined
    }
  }

  const Inner = () => {
    return (
      <Tooltip placement={tooltipPlacement} title={renderTooltip()}>
        <span>
          {renderBeforeIcon()}
          <span style={{ color: textColor, textDecoration: underlineText ? 'underline' : undefined }}>{renderText()}</span>
          {after && <PerIcon name={after} size={iconSize()} variant="clickable" />}
        </span>
      </Tooltip>
    )
  }

  return (
    <>
      {asLink ? (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        <a className={btn + ' ' + modifiers.join(' ')} href={asLink} download="" {...(props as any)}>
          <Inner />
        </a>
      ) : (
        <button className={btn + ' ' + modifiers.join(' ')} {...props} disabled={disabled || processingIsActive}>
          <Inner />
        </button>
      )}
    </>
  )
}

export default PerButton
